import { apiErrorMessage } from '@agos/api-client'
import {
  getGetFiskalClosuresQueryKey,
  getGetFiskalDrawerStatusQueryKey,
  postFiskalTest,
  useGetFiskalClosures,
  useGetFiskalDrawerStatus,
  useGetFiskalRegisters,
  useGetSettingsFiskal,
  usePostFiskalClosure,
} from '@agos/api-client/fiskal'
import {
  T,
  formatCents,
  formatDateTime,
  registerIds,
  registerName,
  SHIPPED_REGISTER_NAMES,
  tt,
} from '@agos/shared'
import {
  BUTTON_GHOST,
  cn,
  type Column,
  ConfirmDialog,
  DataTable,
  DateRangePicker,
  FilterBar,
  FilterSelect,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
  ReadFailedBlock,
} from '@agos/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpFromLine,
  Banknote,
  CalendarCheck,
  ClipboardCheck,
  CheckCircle2,
  ClipboardList,
  Clock,
  History,
  Info,
  Receipt,
  RefreshCw,
  Truck,
  Utensils,
  Wifi,
  WifiOff,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import { readConnection, type KasaId } from '../fiskal-settings/settings-form'
import {
  failed,
  filterHistory,
  historyTotals,
  kasaColor,
  latestOfType,
  movedCents,
  movesCash,
  performedByLabel,
  typeColor,
  typeLabel,
  type ClosureRecord,
  type KasaFilter,
  lastClosureState,
} from './closure-history'
import {
  amountForRequest,
  canSubmit,
  checkAmount,
  needsSecondLook,
  readDrawer,
  drawerWindow,
  drawerWindowNote,
  type AmountProblem,
  type DrawerReading,
} from './drawer'
import { declaredClosingConfirmation, declaredClosingNote } from './declared-closing'

/**
 * `/fiskal-closures` — X-reports, Z-reports, and cash in and out of the drawer.
 *
 * Legacy's four tabs, its register selector, its status bar and its history table,
 * with two things it does not do:
 *
 *  - **FB-073.** Four of the eight history columns are structurally empty — the
 *    backend never writes `closure_number`, `receipt_count` or `vat_breakdown`, and
 *    the transport only ever gets `ACK` back from the device — and the money column
 *    is named *Tržby* while holding the cash a deposit moved. The columns are named
 *    for what they contain and the page says once why a report row carries no
 *    figures.
 *  - **FB-074.** A withdrawal is checked against the drawer balance shown directly
 *    above the button. Legacy compares nothing, and nothing downstream does either.
 *
 * A Z-report is irreversible: it zeroes the register's daily counters and prints the
 * document the day is filed under. Legacy's inline two-step confirmation is kept and
 * given the same weight as everything else that reaches the device.
 *
 * Ported from legacy `pages/fiskal-closures.tsx`.
 */

type ClosureTab = 'overview' | 'deposit' | 'withdraw' | 'daily'

const TABS: { id: ClosureTab; label: string; icon: typeof ClipboardList }[] = [
  { id: 'overview', label: tt('Prehľadová uzávierka'), icon: ClipboardList },
  { id: 'deposit', label: tt('Vklad'), icon: ArrowDownToLine },
  { id: 'withdraw', label: tt('Výber'), icon: ArrowUpFromLine },
  { id: 'daily', label: tt('Denná uzávierka'), icon: CalendarCheck },
]

const TAB_TONES: Record<ClosureTab, string> = {
  overview: 'bg-blue-500/10 border-blue-500/30',
  deposit: 'bg-emerald-500/10 border-emerald-500/30',
  withdraw: 'bg-orange-500/10 border-orange-500/30',
  daily: 'bg-red-500/10 border-red-500/30',
}

/** Legacy opens on Vklad, which is the operation somebody runs every morning. */
const FIRST_TAB: ClosureTab = 'deposit'

interface ClosureOutcome {
  ok: boolean
  message: string
  data?: Record<string, unknown>
}

/** The names the port ships with, drawn until a franchise types one over a register — §10A.35. */

export function FiskalClosuresPage() {
  const queryClient = useQueryClient()
  const [kasaId, setKasaId] = useState<KasaId>('restauracia')
  const [tab, setTab] = useState<ClosureTab>(FIRST_TAB)
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [outcome, setOutcome] = useState<ClosureOutcome | null>(null)
  const [pending, setPending] = useState<{
    title: string
    description: string
    run: () => void
  } | null>(null)

  const [kasa, setKasa] = useState<KasaFilter>('all')
  const [search, setSearch] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const { data: settingsData, isLoading: settingsLoading } = useGetSettingsFiskal({ kasaId })
  const settings = useMemo(() => readConnection(settingsData, kasaId), [settingsData, kasaId])

  /**
   * The connection check is a POST because the register has no GET, but it is a
   * question — so it is a query, keyed per register. Legacy fires it again on every
   * tab switch; one key means one probe per register per visit.
   */
  const reachable = useQuery({
    queryKey: ['fiskal-test', kasaId],
    queryFn: () => postFiskalTest({ kasaId }),
    enabled: settings.isEnabled && !settings.virtualMode,
    retry: false,
    staleTime: 30_000,
  })

  const { data: drawerData, isFetching: drawerFetching } = useGetFiskalDrawerStatus(
    { kasaId },
    { query: { enabled: tab === 'deposit' || tab === 'withdraw' } },
  )
  const drawer = useMemo(() => readDrawer(drawerData), [drawerData])

  // §10A.35 - the registers this franchise has, with the pair the port shipped with as the fallback
  // until the call answers. Never empty: a closures screen with no register selected closes nothing.
  const { data: registerData } = useGetFiskalRegisters()
  /* §9.578 - declared above `shown` rather than beside the tab strip, because `filterHistory` reads it too and
   * `const` is not hoisted (§9.487). Hoisted out of `registerButtons`, because the table's own Kasa column needs the same
   * rows. It used `kasaLabel`, a two-entry map, so a third register's closures were badged with a raw
   * `kasa_id` directly under a tab strip showing the name the franchise typed. */
  const registerRows = useMemo(
    () =>
      Array.isArray(registerData)
        ? (registerData as Array<{ kasaId?: string; label?: string }>)
        : [],
    [registerData],
  )
  /* §9.634, FB-949 - three claims come off this read: the table's *Žiadne záznamy uzávierok*, four stat
     cards, and *Posl. uzávierka: nezaznamenaná* in amber. The owner's rows hold 1 678 closures. */
  const {
    data: historyData,
    isError: historyFailed,
    refetch: refetchHistory,
  } = useGetFiskalClosures()
  const records = useMemo<ClosureRecord[]>(
    () => (historyData ?? []) as ClosureRecord[],
    [historyData],
  )
  const shown = useMemo(
    () => filterHistory(records, { kasa, search, from, to }, registerRows),
    [records, kasa, search, from, to, registerRows],
  )
  const totals = historyTotals(shown)
  const lastZReport = latestOfType(records, 'z-report', kasaId)
  /**
   * When this register was last closed, from whichever source may be trusted - §9.410.
   *
   * The history list first because it is what the table below shows, then the drawer's own
   * boundary, which is the unbounded per-register query. Null means neither knows of one, and only
   * then is it safe to say so on screen.
   */
  /* §9.634 - the fallback order is §9.410's, unchanged; what is new is that a **failed** history read no
     longer counts as the list having nothing to say. If the drawer still knows a date, the screen names it. */
  const lastClosure = lastClosureState(
    lastZReport?.createdAt ?? null,
    drawer?.sinceDate ?? null,
    historyFailed,
  )

  const close = usePostFiskalClosure({
    mutation: {
      onSuccess: (result: unknown) => {
        const body = asRecord(result)
        setOutcome({
          ok: true,
          message: readString(body, 'message') ?? tt('Operácia úspešná.'),
          data: asRecord(body?.data) ?? undefined,
        })
        setAmount('')
        setNote('')
        void queryClient.invalidateQueries({ queryKey: getGetFiskalClosuresQueryKey() })
        void queryClient.invalidateQueries({ queryKey: getGetFiskalDrawerStatusQueryKey() })
      },
      // §9.356, FB-636 - **this read the wrong object and so could never succeed.**
      // `asRecord(error)` is the error itself; `ApiError` carries the server's envelope in
      // `error.data`, so `.error` on the error was always undefined and the fallback always won.
      // `FiskalClosureService` has seven distinct refusals and six are not a connection problem -
      // an empty amount field said "could not connect to the till" while the status line above
      // reported it online. `apiErrorMessage` reads `error.data.error`, as §6 requires.
      //
      // The fallback no longer names the till either: it now covers only what the envelope cannot
      // carry, and in those cases what failed is the request to AGOS, not the socket to the till.
      // The server names a genuinely unreachable till itself, with the real reason.
      onError: (error: unknown) =>
        setOutcome({
          ok: false,
          message: apiErrorMessage(error, tt('Operáciu sa nepodarilo vykonať.')),
        }),
    },
  })

  const virtualReady = settings.isEnabled && settings.virtualMode
  const isReady = virtualReady || (settings.isEnabled && reachable.data !== undefined)

  const registerButtons = useMemo(
    () =>
      registerIds(registerData).map((id) => ({
        id,
        label: registerName(id, registerRows, SHIPPED_REGISTER_NAMES),
      })),
    [registerData, registerRows],
  )

  const switchKasa = (next: KasaId) => {
    setKasaId(next)
    setOutcome(null)
  }
  const switchTab = (next: ClosureTab) => {
    setTab(next)
    setOutcome(null)
  }

  const problem = checkAmount(amount, tab === 'withdraw' ? 'withdraw' : 'deposit', drawer)

  const runClosure = (
    // §9.450, FB-771 - `z-report-declared` records a closing performed on the register itself.
    action: 'x-report' | 'z-report' | 'z-report-declared' | 'deposit' | 'withdraw',
  ) => {
    setOutcome(null)
    close.mutate({
      data: movesCash(action)
        ? { kasaId, action, amount: amountForRequest(amount), note: note.trim() }
        : { kasaId, action },
    })
  }

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={tt('Pokladňa Uzávierky')}
        description={tt('Uzávierky, vklady a výbery z pokladne')}
        icon={<Receipt className="w-5 h-5" />}
      />

      <div className="flex flex-col sm:flex-row gap-1 bg-muted/40 rounded-xl p-1 border border-border">
        {/* §10A.35 - a button per configured register. A third one takes money like the other two,
         * and its Z-report is as required of it, so it cannot be a screen that will not show it. */}
        {registerButtons.map((register) => (
          <RegisterButton
            key={register.id}
            active={kasaId === register.id}
            onClick={() => switchKasa(register.id as KasaId)}
            icon={
              register.id === 'rozvoz' ? (
                <Truck className="w-4 h-4" />
              ) : register.id === 'restauracia' ? (
                <Utensils className="w-4 h-4" />
              ) : (
                <Receipt className="w-4 h-4" />
              )
            }
            label={register.label}
            tone={register.id === 'rozvoz' ? 'violet' : 'amber'}
          />
        ))}
      </div>

      <div
        data-register-status={isReady ? 'ready' : 'not-ready'}
        className={cn(
          'rounded-xl border p-4 flex items-center gap-4',
          isReady
            ? 'bg-emerald-500/10 border-emerald-500/30'
            : !settings.isEnabled
              ? 'bg-muted/40 border-border'
              : 'bg-red-500/10 border-red-500/30',
        )}
      >
        <div
          className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
            isReady ? 'bg-emerald-500/20' : !settings.isEnabled ? 'bg-muted' : 'bg-red-500/20',
          )}
        >
          {isReady ? (
            <Wifi className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />
          ) : (
            <WifiOff className="w-5 h-5 text-red-700 dark:text-red-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'font-semibold text-sm',
              isReady
                ? 'text-emerald-700 dark:text-emerald-400'
                : !settings.isEnabled
                  ? 'text-foreground'
                  : 'text-red-700 dark:text-red-400',
            )}
          >
            {registerName(kasaId, registerRows, SHIPPED_REGISTER_NAMES)} -{' '}
            {!settings.isEnabled
              ? tt('kasa nie je aktívna')
              : settings.virtualMode
                ? tt('virtuálna kasa pripravená')
                : reachable.isFetching
                  ? tt('overujem pripojenie...')
                  : reachable.data !== undefined
                    ? tt('pripojená')
                    : tt('nepripojená')}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {!settings.isEnabled
              ? tt('Aktivujte pokladňu v nastaveniach')
              : settings.virtualMode
                ? tt('Doklady sa spracúvajú virtuálne')
                : reachable.data !== undefined
                  ? `${tt('Kasa je online a pripravená na operácie')} - ${settings.kasaHost}:${settings.kasaPort}`
                  : tt('Skontrolujte nastavenia a pripojenie pokladne')}
          </p>
        </div>
        {/* §9.410 - **always rendered**, because the case worth seeing is the one where there is
         * nothing to name. This was guarded on `lastZReport`, so a register that has never been
         * closed showed no *Posl. uzávierka* block at all - and an operator running X-reports,
         * which print a document and reset nothing, had no way to notice.
         *
         * **The fallback order is about what each source may claim.** `latestOfType` reads the
         * fetched history, and `HISTORY_LIMIT` is 100 rows **across both registers**, so a Z older
         * than that is missing from the list without being missing from the register. The drawer's
         * `sinceDate` is `lastSuccessfulZReport` - `ORDER BY created_at DESC LIMIT 1`, per register,
         * unbounded - so only its absence is authoritative, and only then is the closure named as
         * unrecorded. */}
        {/* §9.634, FB-949 - **three states, and the amber warning belongs to exactly one of them.**
          * `unrecorded` is the state the comment above built it for; `unknown` is a read that failed, and
          * saying it here rather than in a banner elsewhere is the point - this is where somebody looks for
          * when the till was last closed. */}
        <div className="shrink-0 text-right" data-last-z={lastClosure.state}>
          <p className="text-xs text-muted-foreground">
            <T>Posl. uzávierka</T>
          </p>
          {lastClosure.state === 'unrecorded' ? (
            <p className="text-xs font-medium flex items-center gap-1 justify-end text-amber-700 dark:text-amber-500">
              <AlertTriangle className="w-3 h-3" />
              <T>nezaznamenaná</T>
            </p>
          ) : lastClosure.state === 'unknown' ? (
            <p className="text-xs font-medium flex items-center gap-1 justify-end text-red-700 dark:text-red-500">
              <AlertTriangle className="w-3 h-3" />
              <T>nepodarilo sa načítať</T>
            </p>
          ) : (
            <p className="text-xs font-medium flex items-center gap-1 justify-end">
              <Clock className="w-3 h-3" />
              {formatDateTime(lastClosure.at)}
            </p>
          )}
        </div>
      </div>

      {!isReady && (
        <Callout tone="amber" title={tt('Pokladňa nie je pripravená')}>
          {!settings.isEnabled
            ? tt('Najskôr aktivujte pokladňu v časti Pokladňa → Nastavenia.')
            : tt(
                'Nie je nadviazané spojenie s fiskálnou kasou. Skontrolujte nastavenia a pripojenie.',
              )}
        </Callout>
      )}

      <TabGroup
        variant="bar"
        tabs={TABS.map((entry) => ({ id: entry.id, label: entry.label, icon: entry.icon }))}
        active={tab}
        onChange={(id) => switchTab(id as ClosureTab)}
      />

      <div className={cn('rounded-xl border p-5 space-y-4', TAB_TONES[tab])} data-closure-tab={tab}>
        {tab === 'overview' && (
          <>
            <TabHeading
              icon={<ClipboardList className="w-4 h-4" />}
              title={tt('Prehľadová uzávierka')}
              hint={tt('Predbežný prehľad stavu pokladne bez uzavretia')}
            />
            <Callout tone="blue">
              <T>
                Prehľadová uzávierka zobrazí aktuálny stav pokladne. Neuzatvára pokladňu ani neruší
                počítadlá.
              </T>
            </Callout>
            <OutcomePanel outcome={outcome} />
            <ActionButton
              onClick={() => runClosure('x-report')}
              disabled={!isReady || close.isPending}
              pending={close.isPending}
              icon={<ClipboardList className="w-4 h-4" />}
              label={tt('Vykonať prehľadovú uzávierku')}
              tone="blue"
            />
          </>
        )}

        {(tab === 'deposit' || tab === 'withdraw') && (
          <>
            <TabHeading
              icon={
                tab === 'deposit' ? (
                  <ArrowDownToLine className="w-4 h-4" />
                ) : (
                  <ArrowUpFromLine className="w-4 h-4" />
                )
              }
              title={tab === 'deposit' ? tt('Vklad do pokladne') : tt('Výber z pokladne')}
              hint={
                tab === 'deposit'
                  ? tt('Vloženie hotovosti do pokladne')
                  : tt('Vybratie hotovosti z pokladne')
              }
            />

            <DrawerPanel
              drawer={drawer}
              fetching={drawerFetching}
              onRefresh={() =>
                void queryClient.invalidateQueries({ queryKey: getGetFiskalDrawerStatusQueryKey() })
              }
            />

            <div className="space-y-4">
              <label className="block space-y-1.5">
                <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
                  {tab === 'deposit' ? tt('Suma vkladu (€)') : tt('Suma výberu (€)')}
                </span>
                <span className="relative block">
                  <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    placeholder="0.00"
                    aria-label={tab === 'deposit' ? tt('Suma vkladu') : tt('Suma výberu')}
                    className="w-full bg-background border border-border rounded-lg pl-10 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </span>
              </label>

              {/* FB-074 - the balance is right above this box and legacy never
                  compares the two. */}
              {problem?.kind === 'over-drawer' && (
                <p
                  className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-500"
                  data-over-drawer-warning
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                  <span>
                    <T>V zásuvke je podľa kasy</T> {formatCents(problem.balanceCents)},{' '}
                    <T>takže vyberáte o</T> <strong>{formatCents(problem.shortByCents)}</strong>{' '}
                    <T>viac, než tam má byť.</T>
                  </span>
                </p>
              )}

              <label className="block space-y-1.5">
                <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
                  {tt('Poznámka (voliteľné)')}
                </span>
                <input
                  type="text"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder={
                    tab === 'deposit' ? tt('Napr. ranný vklad') : tt('Napr. odvod do banky')
                  }
                  aria-label={tt('Poznámka')}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                />
              </label>
            </div>

            <OutcomePanel outcome={outcome} />

            <ActionButton
              onClick={() => {
                if (needsSecondLook(problem) && problem?.kind === 'over-drawer') {
                  setPending({
                    title: tt('Vybrať viac, než je v zásuvke?'),
                    description: `${tt('V zásuvke je podľa kasy')} ${formatCents(problem.balanceCents)}. ${tt('Po tomto výbere bude stav zásuvky negatívny o')} ${formatCents(problem.shortByCents)}.`,
                    run: () => runClosure('withdraw'),
                  })
                  return
                }
                runClosure(tab === 'deposit' ? 'deposit' : 'withdraw')
              }}
              disabled={!isReady || close.isPending || !canSubmit(problem)}
              pending={close.isPending}
              icon={
                tab === 'deposit' ? (
                  <ArrowDownToLine className="w-4 h-4" />
                ) : (
                  <ArrowUpFromLine className="w-4 h-4" />
                )
              }
              label={tab === 'deposit' ? tt('Vykonať vklad') : tt('Vykonať výber')}
              tone={tab === 'deposit' ? 'emerald' : 'orange'}
            />
          </>
        )}

        {tab === 'daily' && (
          <>
            <TabHeading
              icon={<CalendarCheck className="w-4 h-4" />}
              title={tt('Denná uzávierka')}
              hint={tt('Uzavretie pokladne na konci dňa - vynuluje počítadlá')}
            />
            <Callout tone="red" title={tt('Pozor - nezvratná operácia')}>
              <T>
                Denná uzávierka (Z-Report) uzavrie pokladňu, vynuluje denné počítadlá a vytlačí
                súhrnný doklad. Po vykonaní ju nie je možné vrátiť späť.
              </T>
            </Callout>
            <OutcomePanel outcome={outcome} />
            <ActionButton
              onClick={() =>
                setPending({
                  title: tt('Vykonať dennú uzávierku?'),
                  description: tt(
                    'Kasa uzavrie deň, vynuluje denné počítadlá a vytlačí súhrnný doklad. Túto operáciu nie je možné vrátiť späť.',
                  ),
                  run: () => runClosure('z-report'),
                })
              }
              disabled={!isReady || close.isPending}
              pending={close.isPending}
              icon={<CalendarCheck className="w-4 h-4" />}
              label={tt('Vykonať dennú uzávierku')}
              tone="red"
            />

            {/* §9.450, FB-771 - the owner's drawer read 0,00 on the register and 18,65 here. AGOS
             * counts the drawer from the last Z-report **it** recorded, and a záverečka done on the
             * FiskalPro device writes no such row, so the boundary never moved. The register cannot
             * be asked - it answers `ACK` and 0 of 1 153 closures carry a figure - so somebody has
             * to be able to say it happened. */}
            <div className="rounded-xl border border-border/60 bg-muted/30 px-3 py-3 space-y-2">
              <p className="text-xs text-muted-foreground" data-declared-note>
                {declaredClosingNote()}
              </p>
              <ActionButton
                onClick={() =>
                  setPending({
                    title: tt('Uzávierka bola vykonaná v pokladni?'),
                    description: declaredClosingConfirmation(),
                    run: () => runClosure('z-report-declared'),
                  })
                }
                disabled={close.isPending}
                pending={close.isPending}
                icon={<ClipboardCheck className="w-4 h-4" />}
                label={tt('Zaznamenať uzávierku vykonanú v pokladni')}
                tone="orange"
                marker="declare-closing"
              />
            </div>
          </>
        )}
      </div>

      <section className="space-y-4">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <History className="w-4 h-4 text-muted-foreground" />
          <T>História uzávierok</T>
        </h3>

        {/* §9.634, FB-949 - **the figures and the table go together, because they come off one read.** A banner
          * over four noughts is FB-843's shape, and the fourth card is violet at nought, which is this screen's
          * colour for *nothing to see* on a tax figure. The filters go too: narrowing a read that never
          * answered is not a remedy, which is the one thing §9.627 kept a picker for. */}
        {historyFailed ? (
          <ReadFailedBlock
            marker="closures"
            title={tt('Históriu uzávierok sa nepodarilo načítať')}
            onRetry={() => void refetchHistory()}
          >
            <T>
              Neznamená to, že žiadne uzávierky nie sú. Kým sa to nenačíta, čísla nižšie by boli
              nepravdivé.
            </T>
          </ReadFailedBlock>
        ) : (
        <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            label={tt('Záznamov')}
            value={String(totals.count)}
            icon={History}
            color="blue"
          />
          <StatCard
            label={tt('Vklady')}
            value={formatCents(totals.depositedCents)}
            icon={ArrowDownToLine}
            color="emerald"
          />
          <StatCard
            label={tt('Výbery')}
            value={formatCents(totals.withdrawnCents)}
            icon={ArrowUpFromLine}
            color="orange"
          />
          <StatCard
            label={tt('Denných uzávierok')}
            value={String(totals.zReports)}
            icon={CalendarCheck}
            color={totals.errors > 0 ? 'red' : 'violet'}
            sub={totals.errors > 0 ? `${totals.errors} ${tt('so chybou')}` : undefined}
          />
        </div>

        <FilterBar
          search={{ value: search, onChange: setSearch, placeholder: tt('Hľadať uzávierku...') }}
          filters={
            <>
              <FilterSelect
                value={kasa}
                label={tt('Kasa')}
                onChange={(value) => setKasa(value as KasaFilter)}
                options={[
                  { value: 'all', label: tt('Všetky kasy') },
                  { value: 'restauracia', label: tt('Reštaurácia') },
                  { value: 'rozvoz', label: tt('Rozvoz') },
                ]}
              />
              <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
            </>
          }
          actions={
            <button
              type="button"
              onClick={() =>
                void queryClient.invalidateQueries({ queryKey: getGetFiskalClosuresQueryKey() })
              }
              aria-label={tt('Obnoviť')}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              <T>Obnoviť</T>
            </button>
          }
        />

        {/* FB-073 - said once, instead of four dashes per row. */}
        <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
          <T>
            Uzávierky nenesú číslo dokladu, počet dokladov ani rozpis DPH: kasa na X- a Z-report
            odpovedá len potvrdením (ACK) a systém si jej počítadlá nečíta. Suma je preto uvedená
            len pri vkladoch a výberoch.
          </T>
        </p>

        <ClosureTable records={shown} />
        </>
        )}
      </section>

      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={() => {
          pending?.run()
          setPending(null)
        }}
        title={pending?.title ?? ''}
        description={pending?.description}
        confirmLabel={tt('Potvrdiť')}
        variant="danger"
      />
    </div>
  )
}

function ClosureTable({ records }: { records: ClosureRecord[] }) {
  /* §9.578 - the franchise's own register names. Shared by query key with the page above, so this is
   * no extra request, and a refused read falls back to the shipped names rather than to nothing. */
  const { data: registerData } = useGetFiskalRegisters()
  /* Memoised, because the column list below is memoised and reads it: a fresh array every render
   * would either make that memo recompute every render or - if it were left out of the dependency
   * list - keep labelling rows from the registers as they were before the call answered. Lint caught
   * the second, which is the one a person would not notice. */
  const registerRows = useMemo(
    () =>
      Array.isArray(registerData)
        ? (registerData as Array<{ kasaId?: string; label?: string }>)
        : [],
    [registerData],
  )
  const columns: Column<ClosureRecord>[] = useMemo(
    () => [
      {
        key: 'type',
        header: tt('Typ'),
        sortable: true,
        sortValue: (row) => typeLabel(row.type),
        render: (row) => <StatusBadge label={typeLabel(row.type)} color={typeColor(row.type)} />,
      },
      {
        key: 'createdAt',
        header: tt('Dátum'),
        sortable: true,
        sortValue: (row) => new Date(row.createdAt ?? 0).getTime(),
        render: (row) => (
          <span className="text-xs whitespace-nowrap">
            {row.createdAt ? formatDateTime(row.createdAt) : '-'}
          </span>
        ),
      },
      {
        key: 'kasaId',
        header: tt('Kasa'),
        sortable: true,
        sortValue: (row) => registerName(row.kasaId, registerRows, SHIPPED_REGISTER_NAMES),
        render: (row) => (
          <StatusBadge
            label={registerName(row.kasaId, registerRows, SHIPPED_REGISTER_NAMES)}
            color={kasaColor(row.kasaId)}
          />
        ),
      },
      {
        // Named for what it holds. Legacy calls it "Tržby" and shows a deposit in it.
        key: 'moved',
        header: tt('Hotovosť'),
        sortable: true,
        sortValue: (row) => movedCents(row) ?? 0,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (row) => {
          const cents = movedCents(row)
          if (cents === null) return <span className="text-xs text-muted-foreground">-</span>
          return (
            <span
              className={cn(
                'text-xs font-semibold tabular-nums',
                cents < 0
                  ? 'text-orange-700 dark:text-orange-400'
                  : 'text-emerald-700 dark:text-emerald-400',
              )}
            >
              {cents < 0 ? '' : '+'}
              {formatCents(cents)}
            </span>
          )
        },
      },
      {
        key: 'result',
        header: tt('Výsledok'),
        sortable: true,
        sortValue: (row) => row.result ?? '',
        render: (row) =>
          failed(row) ? (
            <StatusBadge label={tt('Chyba')} color="red" icon={AlertTriangle} />
          ) : (
            <StatusBadge label="OK" color="green" />
          ),
      },
      {
        /* §9.414 - **the register's answer is gone and who pressed it is here instead.** The owner:
         * *"who made it, instead of ODPOVEĎ KASY because there is ACK ACK ACK ... which is not
         * important, to make like.. who did that operation?"*
         *
         * The comment this replaces called it *"the only column with real content on a report
         * row"*, and measuring the restore says otherwise: **every** `z-report`'s stored reply is
         * `{"raw": "ACK"}`. Three characters, on exactly the rows an operator cares about.
         *
         * `rawResponse` stays on the payload and in the database - the column leaves the screen,
         * not the record. */
        key: 'performedBy',
        header: tt('Vykonal'),
        sortValue: (row) => performedByLabel(row),
        render: (row) => (
          <span
            className={cn(
              'text-xs',
              (row.performedBy ?? '').trim() === '' && 'text-muted-foreground italic',
            )}
            data-closure-by
          >
            {performedByLabel(row)}
          </span>
        ),
      },
      {
        /* §9.414 - *"we have that POZNÁMKA to add it also there? Which is non mandatory and can be
         * nullable."* It was already collected by `perform(...)` and reached only the **virtual**
         * receipt, so for a real register operation it was discarded - a loss being repaired rather
         * than a field being invented. */
        key: 'note',
        header: tt('Poznámka'),
        render: (row) => (
          <span className="text-xs text-muted-foreground line-clamp-2" data-closure-note>
            {(row.note ?? '').trim() === '' ? '-' : row.note}
          </span>
        ),
      },
    ],
    [registerRows],
  )

  return (
    <div data-closure-history>
      <DataTable<ClosureRecord>
        columns={columns}
        data={records}
        rowKey={(row) => row.id ?? 0}
        pageSize={20}
        compact
        emptyMessage={tt('Žiadne záznamy uzávierok')}
        emptyIcon={<History className="w-10 h-10 mb-3 opacity-30" />}
      />
    </div>
  )
}

function DrawerPanel({
  drawer,
  fetching,
  onRefresh,
}: {
  drawer: DrawerReading | null
  fetching: boolean
  onRefresh: () => void
}) {
  return (
    <div className="bg-card rounded-lg border border-border p-4 space-y-2.5" data-drawer>
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium flex items-center gap-1.5">
          <Banknote className="w-3.5 h-3.5" />
          <T>Aktuálny stav zásuvky</T>
        </p>
        <button
          type="button"
          onClick={onRefresh}
          disabled={fetching}
          aria-label={tt('Aktualizovať stav zásuvky')}
          className={cn(BUTTON_GHOST, 'text-xs')}
        >
          {fetching ? <Spinner className="w-3 h-3" /> : <RefreshCw className="w-3 h-3" />}
        </button>
      </div>

      {drawer ? (
        <div className="space-y-1.5">
          {/* §9.410 - **above the figures, not under them.** The owner read an all-time card total
           * as today's takings, and they were entitled to: the period was an 11px grey footnote
           * *guarded on `sinceDate` being present*, so a register with no recorded Z-uzávierka
           * showed no period at all. The sum is unchanged - narrowing it to today would understate
           * a drawer that really had not been emptied. */}
          {drawerWindowNote(drawerWindow(drawer.sinceDate)) !== '' && (
            <p
              className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-500 pb-1"
              data-drawer-window={drawerWindow(drawer.sinceDate).kind}
            >
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span>
                <T>{drawerWindowNote(drawerWindow(drawer.sinceDate))}</T>
              </span>
            </p>
          )}
          <DrawerLine label={tt('Hotovosť (doklady)')} cents={drawer.cashFromReceiptsCents} />
          {drawer.depositsCents > 0 && (
            <DrawerLine label={tt('+ Vklady')} cents={drawer.depositsCents} tone="emerald" small />
          )}
          {drawer.withdrawalsCents > 0 && (
            <DrawerLine label={tt('− Výbery')} cents={drawer.withdrawalsCents} tone="red" small />
          )}
          <div className="flex justify-between items-center py-1 pt-1.5 border-t border-border">
            <span className="text-xs font-medium">
              <T>Zostatok v zásuvke</T>
            </span>
            <span
              className="text-base font-bold text-emerald-700 dark:text-emerald-400 tabular-nums"
              data-drawer-balance
            >
              {formatCents(drawer.balanceCents)}
            </span>
          </div>

          {drawer.inconsistent && (
            <p className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-500">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span>
                <T>Zostatok nesedí so svojimi časťami - mal by byť</T>{' '}
                {formatCents(drawer.derivedBalanceCents)}.
              </span>
            </p>
          )}

          {drawer.cardLines.map((line) => (
            <DrawerLine key={line.label} label={line.label} cents={line.cents} small />
          ))}

          {drawer.sinceDate && (
            <p className="text-[11px] text-muted-foreground pt-1">
              <T>od</T> {formatDateTime(drawer.sinceDate)} (<T>posl. Z-uzávierka</T>)
            </p>
          )}
        </div>
      ) : fetching ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Spinner className="w-3.5 h-3.5" />
          <T>Načítavam stav...</T>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground py-1">
          <T>Stav zásuvky nie je dostupný.</T>
        </p>
      )}
    </div>
  )
}

function DrawerLine({
  label,
  cents,
  tone,
  small,
}: {
  label: string
  cents: number
  tone?: 'emerald' | 'red'
  small?: boolean
}) {
  const colour =
    tone === 'emerald'
      ? 'text-emerald-700 dark:text-emerald-400'
      : tone === 'red'
        ? 'text-red-700 dark:text-red-400'
        : ''
  return (
    <div
      className={cn(
        'flex justify-between items-center',
        small ? 'py-0.5' : 'py-1 border-b border-border/50',
      )}
    >
      <span className={cn('text-xs text-muted-foreground', colour)}>{label}</span>
      <span className={cn('tabular-nums', small ? 'text-xs' : 'text-sm font-semibold', colour)}>
        {formatCents(cents)}
      </span>
    </div>
  )
}

/*
 * **`PreparedFor` was here and is gone — the owner's answer, `OWNER-QUESTIONS.md` §11.**
 *
 * It drew *Predpripravené údaje pre kasu*: the operation type, the endpoint path, the amount and the
 * register. AGOS had it because the port copied legacy faithfully; legacy has since deleted it, and
 * asked whether AGOS should follow, the owner said **"we dont need it in AGOS"**.
 *
 * Removing a panel is not additive, which is why it was asked rather than assumed (`CHANGES.md`).
 * Nothing else changes: the three screens still say which register they act on, and the endpoint path
 * was the only thing here that appeared nowhere else — an internal detail that meant nothing to
 * whoever is closing the till.
 *
 * **`Cell` stayed.** It was defined next to `PreparedFor` and is used by `OutcomePanel` below, which
 * draws what the register actually *answered* — a different panel and a useful one. Taking it out
 * with the rest compiled clean through lint and failed the build with *Cannot find name 'Cell'*, at
 * `8 successful, 23 total`.
 */

/** One labelled value in a grid. Shared with `OutcomePanel`. */
function Cell({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="bg-muted/40 rounded-lg p-3">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className={cn('font-medium', mono && 'font-mono text-xs')}>{value}</p>
    </div>
  )
}
function OutcomePanel({ outcome }: { outcome: ClosureOutcome | null }) {
  if (!outcome) return null

  if (!outcome.ok) {
    return (
      <div
        data-closure-outcome="error"
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-400"
      >
        <WifiOff className="w-4 h-4 shrink-0" />
        {outcome.message}
      </div>
    )
  }

  const entries = Object.entries(outcome.data ?? {})
  if (entries.length === 0) {
    return (
      <div
        data-closure-outcome="ok"
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400"
      >
        <CheckCircle2 className="w-4 h-4 shrink-0" />
        {outcome.message}
      </div>
    )
  }

  // Legacy splits the keys ending in "(DB)" into their own block — those come from
  // the orders table rather than the register, and saying which is which is the
  // difference between a reconciliation and a coincidence.
  const fromRegister = entries.filter(([key]) => !key.endsWith('(DB)'))
  const fromOrders = entries.filter(([key]) => key.endsWith('(DB)'))

  return (
    <div
      data-closure-outcome="ok"
      className="bg-card rounded-lg border border-emerald-500/30 p-4 space-y-3"
    >
      <p className="text-xs text-emerald-700 dark:text-emerald-400 uppercase tracking-wide font-medium flex items-center gap-2">
        <CheckCircle2 className="w-3.5 h-3.5" />
        {outcome.message}
      </p>
      <div className="grid grid-cols-2 gap-3 text-sm">
        {fromRegister.map(([key, value]) => (
          <Cell key={key} label={key} value={String(value)} />
        ))}
      </div>
      {fromOrders.length > 0 && (
        <div className="space-y-2 pt-1">
          <p className="text-xs text-amber-700 dark:text-amber-400 uppercase tracking-wide font-medium">
            <T>Rozdelenie platieb (z objednávok)</T>
          </p>
          <p className="text-xs text-muted-foreground">
            <T>Súhrn platobných metód z objednávok od poslednej dennej uzávierky.</T>
          </p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {fromOrders.map(([key, value]) => (
              <div key={key} className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <p className="text-amber-700/80 dark:text-amber-400/70 text-xs">{key.replace(' (DB)', '')}</p>
                <p className="text-amber-900 dark:text-amber-200 font-medium">{String(value)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function TabHeading({ icon, title, hint }: { icon: ReactNode; title: string; hint: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-background/60 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-sm">{title}</h3>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
    </div>
  )
}

const CALLOUT_TONES = {
  blue: 'bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-300',
  amber: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300',
  red: 'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-300',
} as const

function Callout({
  tone,
  title,
  children,
}: {
  tone: keyof typeof CALLOUT_TONES
  title?: string
  children: ReactNode
}) {
  return (
    <div className={cn('rounded-lg border p-4 text-sm', CALLOUT_TONES[tone])}>
      <div className="flex items-start gap-3">
        {tone === 'blue' ? (
          <Info className="w-5 h-5 shrink-0 mt-0.5" />
        ) : (
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        )}
        <div className="space-y-1 text-muted-foreground">
          {title && <p className="font-medium">{title}</p>}
          <p>{children}</p>
        </div>
      </div>
    </div>
  )
}

const ACTION_TONES = {
  blue: 'bg-blue-500/20 hover:bg-blue-500/30 border-blue-500/30 text-blue-700 dark:text-blue-300',
  emerald:
    'bg-emerald-500/20 hover:bg-emerald-500/30 border-emerald-500/30 text-emerald-700 dark:text-emerald-300',
  orange:
    'bg-orange-500/20 hover:bg-orange-500/30 border-orange-500/30 text-orange-700 dark:text-orange-300',
  red: 'bg-red-500/20 hover:bg-red-500/30 border-red-500/30 text-red-700 dark:text-red-300',
} as const

function ActionButton({
  onClick,
  disabled,
  pending,
  icon,
  label,
  tone,
  marker,
}: {
  onClick: () => void
  disabled: boolean
  pending: boolean
  icon: ReactNode
  label: string
  tone: keyof typeof ACTION_TONES
  /** §9.450 - a `data-action` for the one button an e2e spec has to find among five. */
  marker?: string
}) {
  return (
    <button
      type="button"
      data-action={marker}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full flex items-center justify-center gap-2 border font-semibold rounded-xl py-2.5 transition-colors disabled:opacity-50',
        ACTION_TONES[tone],
      )}
    >
      {pending ? <Spinner className="w-4 h-4" /> : icon}
      {label}
    </button>
  )
}

function RegisterButton({
  active,
  onClick,
  icon,
  label,
  tone,
}: {
  active: boolean
  onClick: () => void
  icon: ReactNode
  label: string
  tone: 'amber' | 'violet'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all border',
        active
          ? tone === 'amber'
            ? 'bg-amber-400/15 border-amber-400/30 text-amber-700 dark:text-amber-400'
            : 'bg-violet-400/15 border-violet-400/30 text-violet-700 dark:text-violet-400'
          : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/60',
      )}
    >
      {icon}
      {label}
    </button>
  )
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
}

function readString(value: Record<string, unknown> | null, key: string): string | null {
  const field = value?.[key]
  return typeof field === 'string' ? field : null
}

export type { AmountProblem }
